import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { useInactivityTimeout, InactivityWarningModal } from './hooks/useInactivityTimeout';
import { supabase } from './supabase';
import ScrollToTop from './ScrollToTop';

// Pages
import Home            from './pages/Home';
import Listings        from './pages/Listings';
import Product         from './pages/Product';
import Cart            from './pages/Cart';
import Auth            from './pages/Auth';
import ResetPassword   from './pages/ResetPassword';
import SellerDashboard from './pages/SellerDashboard';
import SellerShop      from './pages/SellerShop';
import AdminPanel      from './pages/AdminPanel';
import Onboarding      from './pages/Onboarding';
import NotFound        from './pages/NotFound';

// Layout
import Navbar from './components/Navbar';

// ─── Inactivity wrapper ───────────────────────────────────────────────────────
function InactivityGuard({ children }) {
  const { isLoggedIn } = useApp();
  const { showWarning, secondsLeft, resetTimer, logout } = useInactivityTimeout(isLoggedIn);

  return (
    <>
      {children}
      {showWarning && (
        <InactivityWarningModal
          secondsLeft={secondsLeft}
          onStayLoggedIn={resetTimer}
          onLogout={logout}
        />
      )}
    </>
  );
}

// ─── Protected route ─────────────────────────────────────────────────────────
function ProtectedRoute({ children, adminOnly = false }) {
  const { isAdmin } = useApp();
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    // Check existing session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
      setChecking(false);
    });

    // Also listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setHasSession(!!session);
      setChecking(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (checking) return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(245,240,232,0.4)', fontSize: '0.9rem' }}>
      Loading...
    </div>
  );
  if (!hasSession) return <Navigate to="/auth" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;

  return children;
}

// ─── App shell ────────────────────────────────────────────────────────────────
function AppShell() {
  return (
    <InactivityGuard>
      <ScrollToTop />
      <Navbar />
      <Routes>
        {/* Public */}
        <Route path="/"                element={<Home />} />
        <Route path="/listings"        element={<Listings />} />
        <Route path="/product/:id"     element={<Product />} />
        <Route path="/cart"            element={<Cart />} />
        <Route path="/login"           element={<Auth />} />
        <Route path="/auth"            element={<Auth />} />
        <Route path="/seller/auth"     element={<Auth />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/onboarding"      element={<Onboarding />} />
        <Route path="/shop/:id"        element={<SellerShop />} />

        {/* Authenticated */}
        <Route path="/seller/dashboard" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />

        {/* Redirect old routes */}
        <Route path="/dashboard"  element={<Navigate to="/seller/dashboard" replace />} />
        <Route path="/account"    element={<Navigate to="/seller/dashboard" replace />} />

        {/* Admin only */}
        <Route path="/admin"      element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </InactivityGuard>
  );
}

export default function App() {
  return (
    <Router>
      <AppProvider>
        <AppShell />
      </AppProvider>
    </Router>
  );
}
